import * as React from 'react';

import { ProjectResourceType } from 'src/constants/project';
import { useDiagram } from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import {
  MappingIdentifier,
  MappingIdentifierType,
  Resource,
} from 'src/containers/ProjectDiagram/types/data';
import { commitDiagramModel } from 'src/containers/ProjectDiagram/utils/commitDiagramModel';
import { setMappingPrimaryIdentifier } from 'src/containers/ProjectDiagram/utils/mapping/setMappingPrimaryIdentifier';
import { NO_SPECIAL_CHARACTERS_MATCHER } from 'src/containers/ProjectMapping/createNewIdentifierDelimiterItemRenderer';
import { MultiSelectIdentifierItem } from 'src/containers/ProjectMapping/types';
import { projectResourceSelector } from 'src/selectors/projectResource';
import {
  ProjectFileUpload,
  ProjectVirtualGraph,
} from 'src/store/state/projects';
import { getNonEmptyPreviewRow } from 'src/utils/csv/getNonEmptyPreviewRow';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

export const useAddPartToCompositeIdentifier = (
  mappingKey: string,
  currentPrimaryIdentifier: MappingIdentifier
) => {
  const diagram = useDiagram();

  return React.useCallback(
    (item: MultiSelectIdentifierItem) => {
      if (!diagram) {
        return;
      }
      // trim whitespace from delimiters when adding a part
      const valueToAdd =
        item.type === MappingIdentifierType.DELIMITER
          ? item.value.trim()
          : item.value;
      // check for special characters in the identifier and do not add them
      if (
        !valueToAdd.match(NO_SPECIAL_CHARACTERS_MATCHER) &&
        item.type === MappingIdentifierType.DELIMITER
      ) {
        return;
      }

      commitDiagramModel(
        diagram,
        setMappingPrimaryIdentifier({
          key: mappingKey,
          primaryIdentifier: {
            ...currentPrimaryIdentifier,
            parts: [
              ...currentPrimaryIdentifier.parts,
              {
                type: item.type,
                value: valueToAdd,
              },
            ],
          },
          skipMappingRelationships: Boolean(
            currentPrimaryIdentifier.parts.length
          ),
        })
      );
    },
    [diagram, mappingKey, currentPrimaryIdentifier]
  );
};

export const useCompositeIdentifierPreview = ({
  identifier,
  resourceData,
}: {
  identifier: MappingIdentifier;
  resourceData: Resource | null;
}) => {
  const diagram = useDiagram();
  const { projectId } = useRouterParams();
  const projectResource = useReduxSelector(
    projectResourceSelector(projectId, resourceData?.value, resourceData?.type)
  );
  const [preview, setPreview] = React.useState('');
  const identifierBaseIri = identifier.baseIri;
  const identifierParts = identifier.parts;

  React.useEffect(() => {
    if (
      projectResource &&
      resourceData?.type === ProjectResourceType.FILE_UPLOAD
    ) {
      const fileUpload = projectResource as ProjectFileUpload;

      const preview = getNonEmptyPreviewRow(
        fileUpload.contents,
        fileUpload.options
      );

      let previewString = '';
      identifierParts.forEach((identifierPart) => {
        if (identifierPart.type === MappingIdentifierType.DELIMITER) {
          previewString = previewString.concat(identifierPart.value);
        } else {
          previewString = previewString.concat(
            // either use example value for field, or the field name
            // if the example doesn't exist (i.e. all empty cells in preview)
            preview[identifierPart.value] || identifierPart.value
          );
        }
      });
      setPreview(encodeURIComponent(previewString));
    }

    if (
      projectResource &&
      resourceData?.type === ProjectResourceType.VIRTUAL_GRAPH
    ) {
      const virtualGraph = projectResource as ProjectVirtualGraph;
      let previewString = '';
      identifierParts.forEach((identifierPart) => {
        if (identifierPart.type === MappingIdentifierType.DELIMITER) {
          previewString = previewString.concat(identifierPart.value);
        } else {
          const fieldIndex = virtualGraph.fields.findIndex(
            (field) => field.value === identifierPart.value
          );
          if (fieldIndex >= 0) {
            let entry = '';
            let rowIndex = 0;
            while (rowIndex < virtualGraph.previewRows.length) {
              const row = virtualGraph.previewRows[rowIndex];
              entry = row[fieldIndex] || '';
              if (entry) {
                break;
              }
              rowIndex += 1;
            }
            previewString = previewString.concat(
              // either use example value for field, or the field name
              // if the example doesn't exist (i.e. all empty cells in preview)
              entry || identifierPart.value
            );
          }
        }
      });
      setPreview(encodeURIComponent(previewString));
    }
  }, [
    identifierBaseIri,
    identifierParts,
    projectResource,
    resourceData,
    diagram,
    projectId,
    setPreview,
  ]);
  return preview;
};
